import { reduceAction } from '@elab/core/store/helpers';

import getTypes from './types';
import getSelectors, { _getDataField } from './selectors';

export const initialState = state => ({
    ...state,
    data: {},
    step: 1,
    isFocused: false,
    metadata: {},
    submitting: false,
    loading: false,
    stepValidationLoading: false,
    globalMessage: {},
    errorsText: { validation: '', other: '' },
    submitUrl: '',
    submitMethod: '',
    isAddressValid: true,
    isNestedAddressValid: true,
    addressSuggestions: {
        suggestions: [],
        visible: false,
        sourceValue: '',
        fieldName: '',
    },
});

const _cloneStateForFieldData = (state, name) => {
    const simpleFieldName = name ? name.split('.')[0] : '';
    return {
        ...state,
        data: {
            ...state.data,
            ...(state.data[simpleFieldName] && {
                [simpleFieldName]: JSON.parse(JSON.stringify(state.data[simpleFieldName])),
            }),
        },
    };
};

const createReducer = storeName => {
    const types = getTypes(storeName);
    return (state = initialState({}), action) => {
        switch (action.type) {
            case types.INITIALIZE:
                return action.payload ? { ...state, ...action.payload } : state;
            case types.SET_INITIAL_DATA:
                return { ...initialState(state), ...(action.payload || {}) };
            case types.SET_LOADING:
                return reduceAction(state, 'loading', !!action.payload);
            case types.SET_STEP_VALIDATION_LOADING:
                return reduceAction(state, 'stepValidationLoading', !!action.payload);
            case types.SET_REGISTERED_USER:
                return reduceAction(state, 'registeredUser', action.payload);
            case types.SET_GLOBAL_MESSAGE:
                return { ...state, globalMessage: action.payload || {} };
            case types.SET_ERRORS_TEXT:
                return { ...state, errorsText: action.payload || {} };

            case types.SET_FOCUSED:
                return reduceAction(state, 'isFocused', true);
            case types.SET_UNFOCUSED:
                return reduceAction(state, 'isFocused', false);
            case types.SET_STEP:
                return reduceAction(state, 'step', action.payload);
            case types.SET_STEP_BACK:
                return { ...state, step: state.step - 1 };
            case types.SET_SUBMIT_URL:
                return reduceAction(state, 'submitUrl', action.payload);
            case types.SET_SUBMIT_METHOD:
                return reduceAction(state, 'submitMethod', action.payload);
            case types.SET_SUCCESS_REDIRECT_LINK:
                return reduceAction(state, 'successRedirectLink', action.payload);

            case types.SET_SINGLE_ERROR_MODE: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const dataField = _getDataField(newState.data, action.payload.name);
                if (dataField) {
                    dataField.singleErrorMode = action.payload.value;
                }
                return newState;
            }

            case types.SET_DATA_FIELD_VALUE: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const dataField = _getDataField(newState.data, action.payload.name);
                if (dataField && typeof dataField === 'object') {
                    const { value, save } = action.payload;
                    if (typeof dataField === 'object') {
                        dataField.value = value;
                        dataField.validated = false;
                    }
                    if (save) {
                        dataField.savedValue = value;
                    }
                }
                return newState;
            }

            case types.SET_DATA_FIELD_VALUES: {
                const { fieldName, values } = action.payload;
                return {
                    ...state,
                    data: {
                        ...state.data,
                        [fieldName]: {
                            ...state.data[fieldName],
                            values,
                        },
                    },
                };
            }

            case types.FOCUS_FIELD: {
                const newState = _cloneStateForFieldData(state, action.payload);
                const dataField = _getDataField(newState.data, action.payload);
                if (dataField) {
                    dataField.focused = true;
                    dataField.validated = true;
                }
                return newState;
            }
            case types.BLUR_FIELD: {
                const newState = _cloneStateForFieldData(state, action.payload);
                const dataField = _getDataField(newState.data, action.payload);
                if (dataField) {
                    dataField.focused = false;
                }
                return newState;
            }
            case types.TOUCH_FIELD: {
                const newState = _cloneStateForFieldData(state, action.payload);
                const dataField = _getDataField(newState.data, action.payload);
                if (dataField) {
                    dataField.touched = true;
                }
                return newState;
            }
            case types.REMOVE_FIELD_ITEM: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const { name, index } = action.payload;
                const field = _getDataField(newState.data, name);
                field.value = field.value.filter((_, ix) => ix !== index);
                return newState;
            }
            case types.SET_DATA_FIELD_ERROR: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const field = _getDataField(newState.data, action.payload.name);
                if (field) {
                    field.error = action.payload.error;
                }
                return newState;
            }
            case types.SET_DATA_FIELD_TYPE_ERROR: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const field = _getDataField(newState.data, action.payload.name);
                if (field) {
                    field.typeError = action.payload.typeError;
                }
                return newState;
            }
            case types.SET_DATA_FIELD_TYPE: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const field = _getDataField(newState.data, action.payload.name);
                if (field) {
                    field.type = action.payload.type;
                }
                return newState;
            }
            case types.ADD_DATA_FIELD_LINK: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const field = _getDataField(newState.data, action.payload.name);
                if (field) {
                    field.links = field.links || [];
                    field.links.push(action.payload);
                }
                return newState;
            }
            case types.SET_IS_CONSTRAINT_VALID: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const { name, constraintIdx, isValid } = action.payload;
                const selectors = getSelectors();
                const constraint = selectors.getDataFieldConstraintByIndex(
                    newState,
                    name,
                    constraintIdx,
                );
                if (constraint) {
                    constraint.valid = isValid;
                }
                return newState;
            }
            case types.SET_SCROLL_TO_FIELD: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const field = _getDataField(newState.data, action.payload.name);
                if (field) {
                    field.scrollToField = action.payload.value;
                }
                return newState;
            }

            case types.SET_ADDRESS_VALIDATION:
                return reduceAction(state, 'isAddressValid', !!action.payload);

            case types.SET_NESTED_ADDRESS_VALIDATION:
                return reduceAction(state, 'isNestedAddressValid', !!action.payload);

            case types.SHOW_ADDRESS_SUGGESTIONS_LIST:
                return {
                    ...state,
                    addressSuggestions: {
                        suggestions: action.payload.suggestions || [],
                        sourceValue: action.payload.sourceValue || '',
                        fieldName: action.payload.fieldName || '',
                        visible: true,
                    },
                };

            case types.HIDE_ADDRESS_SUGGESTIONS_LIST:
                return {
                    ...state,
                    addressSuggestions: {
                        suggestions: [],
                        sourceValue: '',
                        fieldName: '',
                        visible: false,
                    },
                };

            case types.DATA_FIELD_VALIDATION_DONE: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const dataField = _getDataField(newState.data, action.payload.name);
                if (dataField) {
                    dataField.validated = true;
                }
                if (action.payload.deferredValidated && dataField) {
                    dataField.deferredValidated = true;
                }
                return newState;
            }

            case types.UPDATE_CONSTRAINTS: {
                const { updatedFieldName, updatedConstraints } = action.payload || {};
                return {
                    ...state,
                    data: {
                        ...state.data,
                        [updatedFieldName]: {
                            ...state.data[updatedFieldName],
                            constraints: updatedConstraints || [],
                        },
                    },
                };
            }

            case types.SET_DATA_FIELD_READONLY_STATUS: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const dataField = _getDataField(newState.data, action.payload.name);
                if (dataField) {
                    dataField.readOnly = action.payload.status;
                }
                return newState;
            }

            case types.SET_DATA_FIELD_DISABLED_STATUS: {
                const newState = _cloneStateForFieldData(state, action.payload.name);
                const dataField = _getDataField(newState.data, action.payload.name);
                if (dataField) {
                    dataField.disabled = action.payload.status;
                }
                return newState;
            }
            case types.DISABLE_STEP1_BUTTON:
                return {
                    ...state,
                    ...action.payload,
                };
            case types.SET_LOYALTY_FLAG:
                return {
                    ...state,
                    isLoyaltyFlag: action.payload,
                };

            default:
                return state;
        }
    };
};

export default createReducer;
